import { Dispatch } from 'redux';
import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { RootState } from '../index';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';
import { withJurisdictionKeys, withLanguage } from '../../utils/aiPreferences';
import {
  PREFERENCES_HYDRATING,
  PREFERENCES_HYDRATED,
  PREFERENCES_HYDRATION_FAILED,
} from './DataAction';

/**
 * Dual-write guard: only rewrite the jurisdiction keys when this update
 * actually carries one. An update that only touches language or voice must
 * leave the stored jurisdiction alone.
 */
const dualWriteJurisdiction = <T extends Record<string, any>>(settings: T): T =>
  'jurisdiction' in settings || 'CountryOfJurisdiction' in settings
    ? withJurisdictionKeys(settings)
    : settings;

/**
 * Same guard for the AI output language: normalize it when the update carries
 * one, and leave the stored value untouched when it does not.
 */
const normalizeLanguage = <T extends Record<string, any>>(settings: T): T =>
  'language' in settings ? withLanguage(settings) : settings;

/**
 * Deep merge preferences - merges new settings with existing ones
 * Ensures we don't overwrite existing data with empty objects
 */
const mergePreferences = (
  existing: Record<string, any>,
  updates: Record<string, any>
): Record<string, any> => {
  return {
    ...existing,
    ...updates,
  };
};

/**
 * Get complete preferences object by merging current state with updates
 * Safely handles missing or empty preference objects
 */
const getCompletePreferences = (
  currentPrefs: any,
  updates: { general_settings?: any; ai_settings?: any }
) => {
  const current = currentPrefs || {};
  const currentGeneral = current.general_settings || {};
  const currentAI = current.ai_settings || {};

  return {
    general_settings: updates.general_settings
      ? mergePreferences(currentGeneral, updates.general_settings)
      : currentGeneral,
    ai_settings: updates.ai_settings
      ? mergePreferences(currentAI, updates.ai_settings)
      : currentAI,
  };
};

/**
 * Set user preferences for both general and AI settings
 * Uses the unified /onboarding/set-preferences endpoint
 * Automatically updates Redux state with the response (preferences only)
 *
 * Important: This action only updates preferences, not other user data
 * This ensures we don't overwrite user info set by other components (like Sidebar)
 */
export const setUserPreferences = (preferences: {
  general_settings?: { language?: string; theme?: string; currency?: string };
  ai_settings?: {
    jurisdiction?: string;
    CountryOfJurisdiction?: string;
    language?: string;
    speaker_voice?: string;
  };
}) => async (dispatch: Dispatch<any>) => {
  try {
    const response = await apis.post('/onboarding/set-preferences', preferences);
    if (response.data.success) {
      // Update only preferences in Redux state, preserving other user data
      // This prevents clearing user info set by other components (like Sidebar)
      if (response.data.user?.preferences) {
        dispatch({
          type: 'UPDATE_USER_PREFERENCES',
          payload: {
            preferences: response.data.user.preferences
          }
        });
      }

      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Preferences updated successfully',
      //   modalType: 'success',
      //   duration: 3000
      // }));
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to set preferences');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to set preferences');
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 5000
    }));
    throw error;
  }
};

/**
 * Fetch current user information and preferences
 * Updates Redux state with preferences only (not entire user info)
 * Uses the /onboarding/get-user-info endpoint
 * This should be called from the settings page to ensure preferences are loaded
 *
 * Important: This action only updates preferences, not other user data
 * This ensures we don't overwrite user info set by other components (like Sidebar)
 * For full user info updates during app initialization, use userInformation() from DataAction instead
 */
export const fetchUserInfoWithPreferences = () => async (dispatch: Dispatch<any>) => {
  try {
    const response = await apis.get('/onboarding/get-user-info');
    if (response.data.success) {
      // Update only preferences in Redux, preserving other user data
      // This prevents clearing user info set by other components (like Sidebar)
      if (response.data.user?.preferences) {
        dispatch({
          type: 'UPDATE_USER_PREFERENCES',
          payload: {
            preferences: response.data.user.preferences
          }
        });
      }
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to get user info');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to get user info');
    dispatch(showFeedbackModal({
      showModal: true,
      message: errorMessage,
      modalType: 'error',
      duration: 5000
    }));
    throw error;
  }
};

/**
 * Initialize user preferences if they are missing from the current state
 * This ensures the frontend always has the latest preferences from the backend
 */
export const initializeUserPreferencesIfNeeded = () => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const preferences = state.user?.userInfo?.preferences;

  // Only fetch if preferences are missing or incomplete
  if (!preferences || (Object.keys(preferences).length === 0)) {
    return dispatch(fetchUserInfoWithPreferences() as any);
  }

  return Promise.resolve();
};

/**
 * THE app-boot hydration of the user's AI preferences.
 *
 * Every service that starts with a jurisdiction or an output language reads
 * its default synchronously from `state.user.userInfo.preferences.ai_settings`
 * (see `useAIPreferences`). That only works if something guarantees the
 * section is in the store before those screens mount - and nothing did:
 * `initializeUserPreferencesIfNeeded` above no-ops whenever `preferences` is
 * merely NON-EMPTY, so a profile payload carrying `general_settings` and no
 * `ai_settings` looked "already loaded" and the fetch never happened.
 *
 * This checks for the section that actually matters, and it is dispatched
 * ONCE from the platform bootstrap - never per component.
 *
 * `user` is in the redux-persist whitelist, so on every load after the first
 * the preference is already in the rehydrated store: the check below passes
 * without a request and the selectors preselect on their very first render.
 */
export const hydrateUserPreferences = () => async (
  dispatch: Dispatch<any>,
  getState: () => RootState,
) => {
  const aiSettings = getState().user?.userInfo?.preferences?.ai_settings;

  if (aiSettings && Object.keys(aiSettings).length > 0) {
    dispatch({ type: PREFERENCES_HYDRATED });
    return;
  }

  dispatch({ type: PREFERENCES_HYDRATING });
  try {
    await dispatch(fetchUserInfoWithPreferences() as any);
    dispatch({ type: PREFERENCES_HYDRATED });
  } catch {
    // Non-fatal: unset is a valid state, so the selectors just show their
    // placeholder rather than blocking or spinning forever.
    dispatch({ type: PREFERENCES_HYDRATION_FAILED });
  }
};

/**
 * Update general settings (language, theme, currency)
 * Intelligently merges with existing settings - never overwrites with empty objects
 * Preserves existing AI settings from state
 */
export const updateGeneralSettings = (settings: {
  language?: string;
  theme?: string;
  currency?: string;
}) => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const currentPreferences = state.user?.userInfo?.preferences;

  // Get complete preferences by merging current state with updates
  const completePreferences = getCompletePreferences(currentPreferences, {
    general_settings: settings,
  });

  return dispatch(setUserPreferences(completePreferences) as any);
};

/**
 * Update AI settings (jurisdiction, language, speaker_voice)
 * Intelligently merges with existing settings - never overwrites with empty objects
 * Preserves existing general settings from state
 */
export const updateAISettings = (settings: {
  jurisdiction?: string;
  CountryOfJurisdiction?: string;
  language?: string;
  speaker_voice?: string;
}) => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const state = getState();
  const currentPreferences = state.user?.userInfo?.preferences;

  // Get complete preferences by merging current state with updates.
  // The jurisdiction is dual-written to both keys so the legacy `jurisdiction`
  // and the authoritative `CountryOfJurisdiction` can never drift apart.
  const completePreferences = getCompletePreferences(currentPreferences, {
    ai_settings: normalizeLanguage(dualWriteJurisdiction(settings)),
  });

  return dispatch(setUserPreferences(completePreferences) as any);
};
